import * as ColorUtils from '../../utils/color-utils';
import * as StringUtils from '../../utils/string-utils';
import * as EventUtils from '../../utils/event-utils';
import { assessmentResultModelModule } from '../../models/assessment-result';
import { firebaseServiceModule } from '../firebase/firebase';

const TYPE_MAPPINGS = {
  Candidate: 'Taking Assessment',
  TeamMember: 'Interviewer',
};

const FIREBASE_REF_ROOT = 'liveInterviews/';
const FIREBASE_REF_ACTIVE = FIREBASE_REF_ROOT + 'active/';

export const RUN_TRIGGER_PATH = '/trigger';
export const RUN_TYPE_TESTS = 'tests';
export const RUN_TYPE_SUBMIT = 'attempt';
export const SUBMIT_PATH = '/submitting';
export const SUBMIT_STARTED = 'started';
export const SUBMIT_RUNNING = 'running';
export const SUBMIT_COMPLETED = 'completed';

export const liveInterviewServiceModule = angular
  .module('Q.services.live-interview-service', [
    assessmentResultModelModule,
    firebaseServiceModule,
  ])

  .factory('liveInterviewService', function (firebase, AssessmentResult) {
    let _assessmentResultsWatcher = null;

    let svc = {
      assessmentResults: [],
      watchAssessments,
      unwatchAssessments,
      join,
      destroy,
    };

    return svc;

    /**
     * Start watching for active assessment results.
     *
     * Call unwatchAssessments() when finished
     *
     * @param team Team to watch
     * @returns {Array} Active assessment results array (can be used directly, or via `liveInterviewService.assessmentResults`
     */
    function watchAssessments(team) {
      unwatchAssessments();

      let teamId = team.id || team;

      function updateAssessmentResults(activeIds) {
        svc.assessmentResults.length = 0;
        const ids = [];
        angular.forEach(activeIds, (_, id) => {
          if (!/^\$/.test(id)) {
            ids.push(id);
          }
        });
        AssessmentResult.fetchAllIds(ids, {
          includes: ['team_candidate'],
        }).then((ars) => {
          svc.assessmentResults.push(...ars.filter((ar) => ar.canEdit()));
        });
      }

      firebase.get(FIREBASE_REF_ACTIVE + teamId).then((activeIds) => {
        updateAssessmentResults(activeIds);
        _assessmentResultsWatcher = activeIds.$watch(() =>
          updateAssessmentResults(activeIds),
        );
      });

      return svc.assessmentResults;
    }

    /**
     * Stop watching the active assessment results.
     */
    function unwatchAssessments() {
      if (_assessmentResultsWatcher) {
        _assessmentResultsWatcher();
        _assessmentResultsWatcher = null;
        svc.assessmentResults.length = 0;
      }
    }

    /**
     * Destroys any data associated with the assessment result
     * @param assessmentResult
     */
    function destroy(assessmentResult) {
      firebase.remove(_getRef(assessmentResult));
    }

    /**
     * Joins a live interview, either as a candidate or a team member. Provides methods
     * to manipulate the live interview.
     *
     * Note: please make sure to disconnect() for team members
     *
     * @param assessmentResult AssessmentResult of the live interview.
     * @param user Current user who is joining the live interview
     * @returns {LiveInterview} An object with methods to manipulate the live interview
     */
    function join(assessmentResult, user) {
      // This is the base ref for storing details about this assessment for live interviews
      // The parent paths to this are non-enumerable on Firebase, so you can't loop through
      // them to find existing solutions.
      let firebaseRef = _getRef(assessmentResult);
      let firebaseActiveChallengeRef = firebaseRef + '/activeChallengeId';

      // Enables teams to see candidates who have are connected in a secure-ish fashion.
      // We use the team ID followed by assessmentResult ID, because it doesn't provide a way
      // to see other solutions within Firebase by listing based on team ID, yet a team can
      // quickly identify who's connected by listing the assessmentResult IDs and looking up
      // the associated team candidates.
      let firebaseActiveRef =
        FIREBASE_REF_ACTIVE +
        assessmentResult.teamId +
        '/' +
        assessmentResult.id;
      let unwatch = {};

      let type = TYPE_MAPPINGS[user.$type] || user.$type;
      let isCandidate = type === TYPE_MAPPINGS.Candidate;

      let userProfile = {
        id: user.id,
        name: user.name,
        profileImgFileId: user.profileImg && user.profileImg.fileId,
        type,
        color: ColorUtils.colorFromString(user.name + ':' + type),
      };
      let _usersRef = null;
      let usersList = [];

      let svc = {
        assessmentResult,
        id: assessmentResult.id,
        // tracks whether we're currently connected (so we can avoid errors)
        connected: false,
        userProfile,
        updateUserProfile,
        firebaseRef,
        firebaseActiveChallengeRef,
        setActiveChallengeId,
        firebaseActiveRef,
        disconnect,
        usersList,
        destroy,
        unwatch,
        // TODO: move activeChallenge tracking, current results tracking, etc
        // activeChallenge: null,
      };

      EventUtils.eventEmitter(svc);

      reconnect();

      return svc;

      //// Public Methods

      /**
       * Call after making changes to the user profile to propagate them to Firebase
       */
      function updateUserProfile() {
        if (unwatch.user) unwatch.user.update();
      }

      /**
       * Connects or reconnects to the live interview
       */
      function reconnect() {
        if (svc.connected) return;

        svc.connected = true;
        // connect this user
        unwatch.user = firebase.setWhenConnected(
          firebaseRef + '/users/' + user.id,
          userProfile,
        );

        if (isCandidate) {
          unwatch.candidateActive = firebase.setWhenConnected(
            firebaseActiveRef,
            true,
          );
        }

        firebase.get(firebaseRef + '/users').then((usersRef) => {
          _usersRef = usersRef;
          _updateUsersList();
          let ulUnwatch = usersRef.$watch(_updateUsersList);
          unwatch.usersList = () => {
            usersList.length = 0;
            ulUnwatch();
            _usersRef = null;
          };

          svc.emit('connected');
        });
      }

      function setActiveChallengeId(challengeId) {
        return firebase.set(firebaseActiveChallengeRef, challengeId);
      }

      /**
       * Disconnects from the live interview (removes watchers)
       */
      function disconnect() {
        svc.connected = false;
        angular.forEach(unwatch, (uw, k) => {
          uw();
          delete unwatch[k];
        });
        svc.emit('disconnected');
      }

      /**
       * Destroys the live interview if it is no longer in use
       */
      function destroy() {
        firebase.remove(firebaseRef);
        disconnect();
        svc.emit('destroyed');
      }

      //// Private

      /**
       * Updates the connected users list
       * @private
       */
      function _updateUsersList() {
        usersList.length = 0;
        if (_usersRef) {
          angular.forEach(_usersRef, (props, id) => {
            if (id[0] === '$') return;
            props.$self = id === user.id;
            usersList.push(props);
          });
          usersList.sort((a, b) => {
            if (a.$self) return -1;
            if (b.$self) return 1;
            return StringUtils.compareIgnoreCase(a.name, b.name);
          });
          svc.emit('usersListChanged', usersList);
        }
      }
    }

    //// Private

    function _getRef(assessmentResult) {
      return (
        FIREBASE_REF_ROOT +
        assessmentResult.teamCandidateId +
        '/' +
        assessmentResult.assessmentId
      );
    }
  });
